import { useTourFiltersStore } from '@/entities/filter/store/tour';
import { locales as localesDuration, useTourFilterDurationTitle } from '@/features/filter/duration';
import { locales as localesFormat, useFilterTourFormatTitle } from '@/features/filter/format';
import { useTourFilterPriceTitle, locales as localesPrice } from '@/features/filter/price';
import { locales as localesOutput } from '../../i18/index.ts';
import { mergeLocales } from '@/shared/lib/merge-locales';
import { useTourAdditionalFilterOptions } from './useTourAdditionalFilterOptions.ts';
import type { CurrencyCode } from '@/shared/lib/currency';
import { useTourActivitiesTitle, locales as localesToursActivities } from '@/features/filter/tour-activities';
import { MAX_TYPES_IN_BUTTON } from '@/entities/filter/popup-wrapper/lib/const.ts';

type FilterChipOption = {
  value: string;
  label: string;
  labelItemsCount?: number;
};

function resolveLabelItemsCount(count?: number) {
  if (count === undefined) {
    return undefined;
  }

  return count > MAX_TYPES_IN_BUTTON ? count : undefined;
}

function createFilterChip(
  isActive: boolean,
  value: string,
  label: string,
  itemsCount?: number,
): FilterChipOption | null {
  if (!isActive) {
    return null;
  }

  const labelItemsCount = resolveLabelItemsCount(itemsCount);

  return labelItemsCount === undefined ? { value, label } : { value, label, labelItemsCount };
}

export function useToursFiltersTitles(currencyCode: CurrencyCode) {
  const locales = mergeLocales(localesDuration, localesFormat, localesPrice, localesOutput, localesToursActivities);

  const filtersStore = useTourFiltersStore();

  const { title: titleDuration, isActive: isActiveDuration } = useTourFilterDurationTitle(locales.ru);
  const {
    title: titleTourFormat,
    itemsCount: tourFormatItemsCount,
    isActive: isActiveTourFormat,
  } = useFilterTourFormatTitle(locales.ru);
  const { title: titlePrice, isActive: isActivePrice } = useTourFilterPriceTitle({
    messages: locales.ru,
    currencyCode,
  });
  const {
    title: titleToursActivities,
    itemsCount: toursActivitiesItemsCount,
    isActive: isActiveToursActivities,
  } = useTourActivitiesTitle(locales.ru);
  const {
    isActiveDatesFilter,
    isActiveComfortFilter,
    isActiveActivityLevelFilter,
    isActiveAccommodationFilter,
    isActiveChildrenFilter,
    childrenTitle,
    dateTitle,
    isActivePersonsFilter,
    comfortTitle,
    activityLevelTitle,
    accommodationTitle,
    accommodationGroupsCount,
    personsTitle,
  } = useTourAdditionalFilterOptions(locales.ru);

  const resetters = {
    duration: () => filtersStore.reset('duration'),
    format: () => filtersStore.reset('tourFormat'),
    price: () => filtersStore.reset('price'),
    toursActivities: () => filtersStore.reset('toursActivities'),
    persons: () => filtersStore.reset('persons'),
    date: () => filtersStore.reset('date'),
    children: () => filtersStore.reset('childrenAllowed'),
    comfort_type: () => filtersStore.reset('comfort_type'),
    activity_level_category: () => filtersStore.reset('activity_level_category'),
    accommodation_type: () => filtersStore.reset('accommodation_type'),
  };

  const filterChips = computed(() =>
    [
      createFilterChip(!!isActiveDatesFilter.value, 'date', dateTitle.value ?? ''),
      createFilterChip(isActiveDuration.value, 'duration', titleDuration.value),
      createFilterChip(isActiveTourFormat.value, 'format', titleTourFormat.value, tourFormatItemsCount.value),
      createFilterChip(!!isActiveChildrenFilter.value, 'children', childrenTitle.value),
      createFilterChip(!!isActivePersonsFilter.value, 'persons', personsTitle.value ?? ''),
      createFilterChip(
        !!isActiveComfortFilter.value,
        'comfort_type',
        comfortTitle.value,
        filtersStore.searchState.comfort_type.length,
      ),
      createFilterChip(
        !!isActiveActivityLevelFilter.value,
        'activity_level_category',
        activityLevelTitle.value,
        filtersStore.searchState.activity_level_category.length,
      ),
      createFilterChip(
        !!isActiveAccommodationFilter.value,
        'accommodation_type',
        accommodationTitle.value,
        accommodationGroupsCount.value,
      ),
      createFilterChip(isActivePrice.value, 'price', titlePrice.value),
      createFilterChip(
        isActiveToursActivities.value,
        'toursActivities',
        titleToursActivities.value,
        toursActivitiesItemsCount.value,
      ),
    ].filter((chip): chip is FilterChipOption => chip !== null),
  );

  return {
    filterChips,
    resetters,
  };
}
